// The export affordance itself: a small icon at a table's top right corner that
// appears on hover and saves the rendered rows as CSV (`table-csv.ts`).
//
// IT RENDERS A `<caption>`, which is the whole reason it needs no wrapper and no
// change to any table's layout. A caption is the one element HTML already lets
// you put INSIDE a `<table>` before its header, so a call site is a single line
// dropped in existing markup rather than a new box around it - which matters
// because `table.css` states the house rule that admin styles tables by class
// and no component wraps them. The caption itself is zero height with
// `pointer-events: none`; only the 24px button takes a click, floating over the
// right end of the header band.
//
// A table may have only ONE caption, so a table that already uses its caption
// for a real sentence hosts `<TableExportButton>` inside that caption instead,
// with `rc-table-export-host` on it for the positioning context.
//
// Hover, not a toolbar button: an export is a rare, deliberate act on a console
// with ~40 tables, and forty permanent buttons would be forty pieces of chrome
// competing with the data. It is reachable by keyboard regardless - the button
// is a real button in the tab order, and `:focus-visible` reveals it - so the
// hover is a visual economy, never the only way in.
import { useRef } from "react";

import { downloadBlob } from "../../lib/download";
import { csvBlob, csvFilename, tableToCsv } from "./table-csv";
import "./table-export.css";

export interface TableExportProps {
  /** Filename stem, kebab-case. Dated on the way out ("big-clients" becomes
   *  "big-clients-2026-09-23.csv"), so two exports never collide in a
   *  downloads folder. */
  name: string;
  /** What the table holds, for the button's accessible name. Defaults to the
   *  stem, which reads acceptably ("Download big-clients as CSV") but is worth
   *  overriding where the stem is terse. */
  label?: string;
}

/** The button alone, for a table whose caption is already spoken for. Its host
 *  needs `className="... rc-table-export-host"` or the button positions itself
 *  against whatever ancestor happens to be positioned. */
export function TableExportButton({ name, label }: TableExportProps) {
  const ref = useRef<HTMLButtonElement>(null);

  function save() {
    // `closest` rather than a passed-in ref: the button IS inside the table it
    // exports, so it can find its own subject and no call site wires one up.
    const table = ref.current?.closest("table");
    if (!table) return;
    const csv = tableToCsv(table);
    // Nothing to export (an empty list, or every row was a control row). Saving
    // a header-only file would read as a broken export rather than an empty one.
    if (!csv) return;
    downloadBlob(csvBlob(csv), csvFilename(name, new Date()));
  }

  return (
    <button
      ref={ref}
      type="button"
      className="rc-table-export__button"
      onClick={save}
      title="Download CSV"
      aria-label={`Download ${label ?? name} as CSV`}
    >
      <svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 3v11" />
        <path d="m7.5 10 4.5 4.5 4.5-4.5" />
        <path d="M4 17.5V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1.5" />
      </svg>
    </button>
  );
}

/** Drop this as the FIRST child of a `<table>`. Renders the zero-height caption
 *  that hosts the button. */
export function TableExport(props: TableExportProps) {
  return (
    <caption className="rc-table-export rc-table-export-host">
      <TableExportButton {...props} />
    </caption>
  );
}
